import { isApiError } from '@/lib/api/http-client';
import { getLogger } from '@/lib/logger';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

function readLogScope(meta: unknown, fallback: string): string {
  if (meta && typeof meta === 'object' && 'logScope' in meta) {
    const scope = (meta as Record<string, unknown>).logScope;
    if (typeof scope === 'string') return scope;
  }
  return fallback;
}

// 4xx is usually bad user input → warn (avoids noise). Anything else broke → error.
function severityForError(error: unknown): 'error' | 'warn' {
  if (isApiError(error)) {
    return error.status >= 400 && error.status < 500 ? 'warn' : 'error';
  }

  return 'error';
}

// Only the first key segment (the resource name, a string literal by convention)
// is safe to log. Later segments carry IDs, tokens, and filters — e.g.
// ['verify-login-link', token] — which must never reach the logger.
function safeQueryKeyRoot(queryKey: readonly unknown[]): string {
  const root = queryKey[0];
  return typeof root === 'string' ? root : 'unknown';
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: (failureCount, error: unknown) => {
          if (
            error &&
            typeof error === 'object' &&
            'status' in error &&
            typeof error.status === 'number' &&
            error.status >= 400 &&
            error.status < 500
          ) {
            return false;
          }
          return failureCount < 2;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: true,
        networkMode: 'online',
      },
      mutations: { networkMode: 'online' },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        const scope = readLogScope(query.options.meta, 'lib.query-cache');
        getLogger({ scope })[severityForError(error)]('Query failed', {
          error,
          attributes: { 'query.key.root': safeQueryKeyRoot(query.queryKey) },
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const scope = readLogScope(mutation.options.meta, 'lib.mutation-cache');
        getLogger({ scope })[severityForError(error)]('Mutation failed', { error });
      },
    }),
  });
}
